import React, { useEffect, useRef, useState } from 'react'

import { useQueryClient } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

type QueryKey = (string | number | boolean | Record<string, any> | undefined | null)[]

interface Props {
  children: React.ReactNode
  scrollContainerRef?: React.RefObject<HTMLElement>
  invalidateQueries?: Array<QueryKey | (() => Promise<any> | any)>
}

const CustomPullToRefresh: React.FC<Props> = ({
  children,
  scrollContainerRef,
  invalidateQueries,
}) => {
  const innerRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)

  const pullRef = useRef(0)
  const startYRef = useRef<number | null>(null)
  const startXRef = useRef<number | null>(null)
  const activeRef = useRef(false)

  const MAX_PULL = 17
  const TRIGGER_THRESHOLD = 10

  const dampen = (d: number) => Math.min(MAX_PULL, d * 0.5)

  const isModalOpen = () => {
    return (
      document.body.classList.contains('modal__scrollblock') ||
      !!document.querySelector('.modal.modal--active')
    )
  }

  const isIgnoredTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false

    return !!target.closest(
      '.modal, .stats__overlay, .stats__slider, .stats__data'
    )
  }

  const getScrollTop = () => {
    const el = scrollContainerRef?.current

    if (el && el.scrollHeight > el.clientHeight + 5) {
      return el.scrollTop
    }

    return (
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    )
  }

  const atTop = () => getScrollTop() <= 5

  const setDistance = (val: number) => {
    pullRef.current = val
    setPullDistance(val)
  }

  const resetGesture = () => {
    activeRef.current = false
    startYRef.current = null
    startXRef.current = null
  }

  const handleTouchStart = (e: TouchEvent) => {
    if (isModalOpen() || isIgnoredTarget(e.target)) {
      resetGesture()
      setDistance(0)
      return
    }

    if (!atTop()) {
      resetGesture()
      return
    }

    const t = e.touches[0]
    startYRef.current = t.clientY
    startXRef.current = t.clientX
    activeRef.current = true
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (isModalOpen() || isIgnoredTarget(e.target)) {
      resetGesture()
      setDistance(0)
      return
    }

    if (!activeRef.current) return

    const sy = startYRef.current
    const sx = startXRef.current

    if (sy == null || sx == null) return

    const t = e.touches[0]
    const dy = t.clientY - sy
    const dx = t.clientX - sx

    if (Math.abs(dx) > Math.abs(dy)) {
      resetGesture()
      setDistance(0)
      return
    }

    if (dy > 0 && atTop()) {
      if (e.cancelable) e.preventDefault()

      const next = dampen(dy)
      setDistance(next)
    } else {
      resetGesture()
      setDistance(0)
    }
  }

  const handleTouchEnd = () => {
    if (isModalOpen()) {
      resetGesture()
      setDistance(0)
      return
    }

    const current = pullRef.current

    if (!activeRef.current) {
      setDistance(0)
      resetGesture()
      return
    }

    const shouldRefresh = current >= TRIGGER_THRESHOLD

    resetGesture()

    if (!shouldRefresh) {
      setDistance(0)
      return
    }

    setRefreshing(true)

    let promises: Promise<any>[] = []

    if (invalidateQueries && invalidateQueries.length > 0) {
      promises = invalidateQueries.map(async item => {
        if (typeof item === 'function') {
          return await Promise.resolve(item())
        }

        return await queryClient.invalidateQueries(item)
      })
    } else {
      promises = [
        queryClient.invalidateQueries(['getHomeFeed', 1]),
        queryClient.invalidateQueries(['allStoryPosts']),
      ]
    }

    Promise.allSettled(promises).finally(() => {
      setTimeout(() => {
        setRefreshing(false)
        setDistance(0)
      }, 300)
    })
  }

  useEffect(() => {
    const opts: AddEventListenerOptions = { passive: false }

    window.addEventListener('touchstart', handleTouchStart, opts)
    window.addEventListener('touchmove', handleTouchMove, opts)
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [])

  return (
    <div
      ref={innerRef}
      style={{
        transform: `translateY(${refreshing ? 0 : pullDistance}px)`,
        transition: 'transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)',
        position: 'relative',
      }}
    >
      {refreshing && (
        <div style={spinnerOverlayStyle}>
          <BeatLoader color="#000000" loading={true} size={8} />
        </div>
      )}

      {children}
    </div>
  )
}

export default CustomPullToRefresh

const spinnerOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: '0',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 888,
  pointerEvents: 'none',
  marginTop: '10px',
}